import { config } from '../config';
import { clearAuthToken, getAuthHeaders } from './auth';
import { useWalletStore } from '../store/walletStore';
import { useNetworkStore } from '../store/networkStore';
import toast from 'react-hot-toast';
import type {
  Invoice,
  PublicInvoice,
  PublicCheckoutInvoice,
  CreateInvoiceData,
  PayIntentResponse,
  DashboardStats,
  SavedClient,
  SaveClientData,
  BusinessProfile,
  SaveProfileData,
} from '../types';

const API_BASE = config.apiUrl + '/api';
const API_REQUEST_TIMEOUT_MS = 20_000;
const USER_TOAST_COOLDOWN_MS = 12_000;
const lastToastAtByKey = new Map<string, number>();

class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

function notifyUserError(key: string, message: string): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const last = lastToastAtByKey.get(key);
  if (last && now - last < USER_TOAST_COOLDOWN_MS) return;

  lastToastAtByKey.set(key, now);
  toast.error(message, {
    id: `api-error-${key}`,
    duration: 5500,
  });
}

/**
 * Core fetch wrapper.
 *
 * When `walletAddress` is provided the request is authenticated via
 * getAuthHeaders: a signed-nonce session for extension wallets, or a Privy
 * bearer token for embedded wallets. Both are cached ~5 minutes.
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
  walletAddress?: string,
  retryOnAuthError = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (walletAddress) {
    const { signMessage, _privyGetToken } = useWalletStore.getState();
    try {
      const authHeaders = await getAuthHeaders(walletAddress, signMessage, _privyGetToken);
      Object.assign(headers, authHeaders);
    } catch (error: any) {
      notifyUserError(
        'wallet-auth',
        error?.message || 'Authentication failed. Please reconnect your wallet.'
      );
      throw error;
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), API_REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      notifyUserError(
        'request-timeout',
        'Request timed out. Please check your connection and try again.'
      );
      throw new ApiError('Request timed out. Please try again.', 408);
    }
    notifyUserError('network-error', 'Network error. Please check your connection.');
    throw error;
  } finally {
    clearTimeout(timer);
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && walletAddress && retryOnAuthError) {
      clearAuthToken();
      return request<T>(path, options, walletAddress, false);
    }

    if (response.status === 401) {
      notifyUserError('auth-expired', 'Session expired. Please reconnect your wallet.');
    }

    throw new ApiError(
      // Prefer the human-readable message; error codes stay available via
      // the message content for callers that match on them.
      data?.message || data?.error || `Request failed with status ${response.status}`,
      response.status,
      data?.details
    );
  }

  return data as T;
}

// ─── Invoice API ──────────────────────────────────────────────────

export async function createInvoice(
  data: CreateInvoiceData,
  walletAddress: string
): Promise<Invoice> {
  return request<Invoice>(
    '/invoices',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    walletAddress
  );
}

export async function listInvoices(
  walletAddress: string,
  status?: string,
  limit = 50,
  offset = 0,
  options?: { excludePreview?: boolean; networkPassphrase?: string; createdAfter?: string; createdBefore?: string }
): Promise<{ invoices: Invoice[]; total: number }> {
  const params = new URLSearchParams();
  const networkPassphrase = options?.networkPassphrase || useNetworkStore.getState().networkPassphrase;
  if (status) params.set('status', status);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (options?.excludePreview) params.set('excludePreview', 'true');
  if (networkPassphrase) params.set('networkPassphrase', networkPassphrase);
  if (options?.createdAfter) params.set('createdAfter', options.createdAfter);
  if (options?.createdBefore) params.set('createdBefore', options.createdBefore);
  return request<{ invoices: Invoice[]; total: number }>(
    `/invoices?${params.toString()}`,
    {},
    walletAddress
  );
}

export async function getInvoice(id: string): Promise<PublicCheckoutInvoice> {
  // Preview solo-dev del checkout: devuelve un fixture sin tocar el backend.
  // El guard `import.meta.env.DEV` hace que el bundler elimine esta rama (y el
  // import dinámico de los fixtures) en el build de producción.
  if (import.meta.env.DEV && id.startsWith('mock')) {
    const { getMockInvoice } = await import('../dev/mockInvoices');
    const inv = getMockInvoice(id);
    if (inv) return inv as PublicCheckoutInvoice;
  }
  return request<PublicCheckoutInvoice>(`/invoices/${id}`);
}

export async function getOwnerInvoice(
  id: string,
  walletAddress: string
): Promise<Invoice> {
  // Preview solo-dev del detalle de link: devuelve un fixture sin tocar el backend.
  // El guard `import.meta.env.DEV` + import dinámico eliminan esta rama en producción.
  if (import.meta.env.DEV && id.startsWith('mock')) {
    const { getMockLink } = await import('../dev/mockLinks');
    const inv = getMockLink(id);
    if (inv) return inv;
  }
  return request<Invoice>(`/invoices/${id}/owner`, {}, walletAddress);
}

export async function sendInvoice(
  id: string,
  walletAddress: string
): Promise<Invoice> {
  return request<Invoice>(
    `/invoices/${id}/send`,
    { method: 'POST' },
    walletAddress
  );
}

export async function deleteInvoice(
  id: string,
  walletAddress: string
): Promise<void> {
  return request<void>(
    `/invoices/${id}`,
    { method: 'DELETE' },
    walletAddress
  );
}

export async function cancelInvoice(
  id: string,
  walletAddress: string
): Promise<Invoice> {
  return request<Invoice>(
    `/invoices/${id}/cancel`,
    { method: 'POST' },
    walletAddress
  );
}

// ─── Merchant KYC API ─────────────────────────────────────────────
// Gates fiat (Bre-B) off-ramp invoices on seller identity verification.
// Crypto invoices require none of this.

export type KycStatusValue = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED';

export interface KycStatusView {
  status: KycStatusValue;
  provider: string | null;
  verifiedAt: string | null;
  enforced: boolean;
}

export interface StartKycResult {
  status: KycStatusValue;
  provider: string;
  ref?: string;
  verificationUrl?: string;
}

export async function getKycStatus(walletAddress: string): Promise<KycStatusView> {
  return request<KycStatusView>('/kyc/status', {}, walletAddress);
}

export async function startKyc(walletAddress: string): Promise<StartKycResult> {
  return request<StartKycResult>('/kyc/start', { method: 'POST' }, walletAddress);
}

export async function completeMockKyc(
  walletAddress: string,
  approve = true
): Promise<KycStatusView> {
  return request<KycStatusView>(
    '/kyc/mock/complete',
    { method: 'POST', body: JSON.stringify({ approve }) },
    walletAddress
  );
}

export async function getDashboardStats(
  walletAddress: string,
  options?: { excludePreview?: boolean; networkPassphrase?: string }
): Promise<DashboardStats> {
  const params = new URLSearchParams();
  const networkPassphrase = options?.networkPassphrase || useNetworkStore.getState().networkPassphrase;
  if (options?.excludePreview) params.set('excludePreview', 'true');
  if (networkPassphrase) params.set('networkPassphrase', networkPassphrase);
  const query = params.toString();
  const path = query ? `/invoices/stats?${query}` : '/invoices/stats';
  return request<DashboardStats>(path, {}, walletAddress);
}

// Client API ---------------------------------------------------

export async function listSavedClients(
  walletAddress: string
): Promise<SavedClient[]> {
  return request<SavedClient[]>('/clients', {}, walletAddress);
}

export async function saveClient(
  data: SaveClientData,
  walletAddress: string
): Promise<SavedClient> {
  return request<SavedClient>(
    '/clients',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    walletAddress
  );
}

export async function updateClientFavorite(
  clientId: string,
  isFavorite: boolean,
  walletAddress: string
): Promise<SavedClient> {
  return request<SavedClient>(
    `/clients/${clientId}/favorite`,
    {
      method: 'PATCH',
      body: JSON.stringify({ isFavorite }),
    },
    walletAddress
  );
}

// ─── Business profile API ─────────────────────────────────────────

export async function getBusinessProfile(
  walletAddress: string
): Promise<BusinessProfile | null> {
  return request<BusinessProfile | null>('/profile', {}, walletAddress);
}

export async function saveBusinessProfile(
  data: SaveProfileData,
  walletAddress: string
): Promise<BusinessProfile> {
  return request<BusinessProfile>(
    '/profile',
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    walletAddress
  );
}

// ─── Payment API ──────────────────────────────────────────────────

export async function createPayIntent(
  invoiceId: string,
  senderPublicKey?: string | null,
  networkPassphraseOverride?: string,
  amount?: number | string
): Promise<PayIntentResponse> {
  const networkPassphrase = networkPassphraseOverride || useNetworkStore.getState().networkPassphrase;
  const payload: { networkPassphrase: string; senderPublicKey?: string; amount?: number | string } = {
    networkPassphrase,
  };
  if (senderPublicKey) {
    payload.senderPublicKey = senderPublicKey;
  }
  if (amount !== undefined && amount !== '') {
    payload.amount = amount;
  }
  return request<PayIntentResponse>(`/payments/${invoiceId}/pay-intent`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitPayment(
  invoiceId: string,
  signedTransactionXdr: string
): Promise<{ success: boolean; transactionHash: string; ledger: number }> {
  return request('/payments/submit', {
    method: 'POST',
    body: JSON.stringify({ invoiceId, signedTransactionXdr }),
  });
}

export async function getXlmPrice(): Promise<{ usd: number }> {
  return request<{ usd: number }>('/prices/xlm');
}

export interface WalletBalance {
  asset: string;
  code: string;
  balance: string;
  issuer: string | null;
}

export async function getWalletBalances(
  publicKey: string,
  networkPassphrase?: string
): Promise<{ publicKey: string; balances: WalletBalance[] }> {
  const passphrase = networkPassphrase || useNetworkStore.getState().networkPassphrase;
  const params = new URLSearchParams();
  if (passphrase) params.set('networkPassphrase', passphrase);
  return request(`/wallet/${publicKey}/balances?${params.toString()}`);
}

/** Phase 7: live Reflector FX estimate (e.g. COP). available:false if not in the feed. */
export async function getFxRate(
  symbol: string
): Promise<{ available: boolean; estimate?: boolean; symbol: string; rate?: string; asOf?: string }> {
  return request(`/prices/fx/${encodeURIComponent(symbol)}`);
}

// ── Funding links (bearer "reverse payment links") ─────────────────────────

export interface FundingLinkView {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'CLAIMED' | 'RECLAIMED';
  asset: 'XLM' | 'USDC';
  amount: string;
  escrowAccount: string;
  networkPassphrase: string;
  expiresAt: string | null;
  claimedBy: string | null;
  claimTxHash: string | null;
  createdAt: string;
}

export async function createFundingLink(
  input: {
    asset: 'XLM' | 'USDC';
    amount: number;
    escrowAccount: string;
    networkPassphrase: string;
    expiresAt?: string;
  },
  walletAddress: string
): Promise<{ id: string }> {
  return request('/funding-links', { method: 'POST', body: JSON.stringify(input) }, walletAddress);
}

export async function activateFundingLink(
  id: string,
  creationTxHash: string,
  walletAddress: string
): Promise<FundingLinkView> {
  return request(
    `/funding-links/${id}/activate`,
    { method: 'PATCH', body: JSON.stringify({ creationTxHash }) },
    walletAddress
  );
}

export async function listFundingLinks(walletAddress: string): Promise<FundingLinkView[]> {
  const data = await request<{ links: FundingLinkView[] }>('/funding-links', {}, walletAddress);
  return data.links;
}

export async function getFundingLink(id: string): Promise<FundingLinkView> {
  return request(`/funding-links/${id}`);
}

export async function reportFundingLinkClaimed(id: string, txHash: string): Promise<FundingLinkView> {
  return request(`/funding-links/${id}/claimed`, { method: 'POST', body: JSON.stringify({ txHash }) });
}

export async function reportFundingLinkReclaimed(
  id: string,
  txHash: string,
  walletAddress: string
): Promise<FundingLinkView> {
  return request(
    `/funding-links/${id}/reclaimed`,
    { method: 'POST', body: JSON.stringify({ txHash }) },
    walletAddress
  );
}

// ─── Off-ramp (USDC→COP / Bre-B) API ──────────────────────────────

export interface OffRampQuote {
  quoteId: string;
  sellAsset: string;
  buyAsset: string;
  sellAmount: string;
  buyAmount: string;
  rate: string;
  feeTotal: string;
  expiresAt: string;
}

export interface OffRampIntentResponse {
  anchorTxId: string;
  interactiveUrl?: string | null;
  depositAddress: string;
  memo: string;
  memoType: 'text' | 'id' | 'hash';
  asset: string;
  amount: string;
}

export interface OffRampStatusResponse {
  status: string;
  anchorStatus: string;
}

/** Receiver-only: request a firm USDC→COP quote (advances invoice to AWAITING_ANCHOR). */
export async function offrampQuote(
  invoiceId: string,
  data: { sellAmount: string; payoutAlias: string },
  walletAddress: string
): Promise<OffRampQuote> {
  return request<OffRampQuote>(
    `/invoices/${invoiceId}/offramp/quote`,
    { method: 'POST', body: JSON.stringify(data) },
    walletAddress
  );
}

/** Receiver-only: initiate the SEP-24 withdraw (advances to AWAITING_PAYMENT). */
export async function offrampInitiate(
  invoiceId: string,
  quoteId: string,
  walletAddress: string
): Promise<OffRampIntentResponse> {
  return request<OffRampIntentResponse>(
    `/invoices/${invoiceId}/offramp/initiate`,
    { method: 'POST', body: JSON.stringify({ quoteId }) },
    walletAddress
  );
}

/** Public: build the USDC payment XDR the payer signs (payer → anchor, exact memo).
 * Pass `sourceAsset` (non-USDC) to pay via a path payment (Phase 5). */
export async function offrampPayIntent(
  invoiceId: string,
  senderPublicKey: string,
  networkPassphraseOverride?: string,
  sourceAsset?: string
): Promise<{
  transactionXdr: string;
  networkPassphrase: string;
  memo: string;
  depositAddress: string;
  asset: string;
  amount: string;
  sendMax?: string;
  sourceAmount?: string;
  sendAsset?: string;
}> {
  const networkPassphrase = networkPassphraseOverride || useNetworkStore.getState().networkPassphrase;
  return request(`/invoices/${invoiceId}/offramp/pay-intent`, {
    method: 'POST',
    body: JSON.stringify({ senderPublicKey, networkPassphrase, sourceAsset }),
  });
}

/** Public: preview a path payment (how much sourceAsset to send). Phase 5. */
export async function offrampPathQuote(
  invoiceId: string,
  sourceAsset: string,
  networkPassphraseOverride?: string
): Promise<
  | { found: false }
  | { found: true; sendAssetCode: string; sourceAmount: string; sendMax: string }
> {
  const networkPassphrase = networkPassphraseOverride || useNetworkStore.getState().networkPassphrase;
  return request(`/invoices/${invoiceId}/offramp/path-quote`, {
    method: 'POST',
    body: JSON.stringify({ sourceAsset, networkPassphrase }),
  });
}

/** Public: submit the signed off-ramp payment (advances to PROCESSING). */
export async function offrampSubmit(
  invoiceId: string,
  signedTransactionXdr: string,
  depositAddress: string
): Promise<{ success: boolean; transactionHash: string; ledger: number }> {
  return request(`/invoices/${invoiceId}/offramp/submit`, {
    method: 'POST',
    body: JSON.stringify({ invoiceId, signedTransactionXdr, depositAddress }),
  });
}

/** Public: poll anchor status; drives PROCESSING → SETTLING → SETTLED_FIAT. */
export async function offrampStatus(invoiceId: string): Promise<OffRampStatusResponse> {
  return request<OffRampStatusResponse>(`/invoices/${invoiceId}/offramp/status`);
}

/**
 * Payer-driven amount for an OPEN-AMOUNT Bre-B invoice. Sets the total, quotes,
 * and initiates server-side so the invoice advances to AWAITING_PAYMENT.
 */
export async function offrampSetAmount(
  invoiceId: string,
  sellAmount: number | string
): Promise<{ quoteBuyAmount: string | null; depositAddress: string | null; total: string }> {
  return request(`/invoices/${invoiceId}/offramp/set-amount`, {
    method: 'POST',
    body: JSON.stringify({ sellAmount }),
  });
}

// ─── Waitlist (coming-soon fiat rails: Pix / Transferência 3.0) ────

export async function joinWaitlist(input: {
  email: string;
  rail: 'PIX' | 'TRANSFERENCIA_30';
  country?: string;
  wallet?: string;
}): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/waitlist', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getPaymentStatus(
  invoiceId: string
): Promise<{
  invoiceId: string;
  status: string;
  transactionHash: string | null;
  ledgerNumber: number | null;
  paidAt: string | null;
}> {
  return request(`/payments/${invoiceId}/status`);
}

// ─── Off-ramp estimate (public payment page) ──────────────────────
export interface OfframpEstimate {
  available: boolean;
  buyAmount?: string;
  rate?: string;
  belowMinimum?: boolean;
  minCop?: number;
  minUsdc?: string;
}

export async function getOfframpEstimate(invoiceId: string): Promise<OfframpEstimate> {
  return request<OfframpEstimate>(`/invoices/${invoiceId}/offramp/estimate`);
}

// ─── Scan-session handoff API ─────────────────────────────────────
export interface ScanSessionCreated {
  token: string;
  expiresAt: number;
}

export type ScanSessionState =
  | { status: 'pending' }
  | { status: 'ready'; llave: string };

export async function createScanSession(walletAddress: string): Promise<ScanSessionCreated> {
  return request<ScanSessionCreated>('/profile/scan-session', { method: 'POST' }, walletAddress);
}

export async function getScanSession(token: string, walletAddress: string): Promise<ScanSessionState> {
  return request<ScanSessionState>(`/profile/scan-session/${token}`, {}, walletAddress);
}
